import { Users } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import type { BusinessSubscriptions } from '../../types/businessMetrics';
import {
  EMPTY_VALUE_LABEL,
  buildCohortTable,
  formatBusinessCount,
  formatBusinessPercent,
  formatPlanMix,
} from '../../types/businessMetricsView';
import { BusinessPanel, NotApplicableNote, StatGrid, StatTile } from './businessPanelParts';

function CohortTable({ subscriptions }: { subscriptions: BusinessSubscriptions }) {
  const table = buildCohortTable(subscriptions.cohorts);
  if (table.rows.length === 0) {
    return <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">코호트 데이터가 없습니다.</p>;
  }
  return (
    <div className="overflow-x-auto rounded-md border">
      <Table aria-label="월별 구독 시작 코호트 유지" className="min-w-[420px]">
        <TableHeader>
          <TableRow>
            <TableHead>시작 월</TableHead>
            <TableHead className="text-right">시작</TableHead>
            {table.offsets.map((offset) => (
              <TableHead key={offset} className="text-right">{offset === 0 ? '시작 월' : `${offset}개월 뒤`}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {table.rows.map((row) => (
            <TableRow key={row.cohortMonth}>
              <TableCell className="whitespace-nowrap font-medium">{row.cohortMonth}</TableCell>
              <TableCell className="text-right tabular-nums">{formatBusinessCount(row.started)}</TableCell>
              {row.cells.map((cell) => (
                <TableCell key={cell.offset} className="whitespace-nowrap text-right tabular-nums">{cell.label}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function SubscriptionPanel({ subscriptions }: { subscriptions: BusinessSubscriptions | null }) {
  if (!subscriptions) {
    return (
      <BusinessPanel title="구독" testId="business-subscription-panel" icon={Users}>
        <NotApplicableNote reason="이 앱에는 구독 데이터 경로가 없습니다." />
      </BusinessPanel>
    );
  }
  const s = subscriptions;
  const lifetime = s.averageLifetimeDays === null ? EMPTY_VALUE_LABEL : `${formatBusinessCount(s.averageLifetimeDays)}일`;
  return (
    <BusinessPanel
      title="구독"
      testId="business-subscription-panel"
      icon={Users}
      footer={`이탈률은 기간 시작 활성 ${formatBusinessCount(s.activeAtStart)}명 기준의 pay 계산값입니다. 계산할 수 없으면 "${EMPTY_VALUE_LABEL}" 로 표시합니다.`}
    >
      <div className="space-y-4">
        <StatGrid>
          <StatTile label="활성" value={formatBusinessCount(s.active)} hint={`유료 ${formatBusinessCount(s.paidActive)} · 지급 ${formatBusinessCount(s.grantedActive)}`} />
          <StatTile label="신규" value={formatBusinessCount(s.newInPeriod)} />
          <StatTile label="해지" value={formatBusinessCount(s.canceledInPeriod)} />
          <StatTile label="해지 예약" value={formatBusinessCount(s.cancelScheduled)} tone={s.cancelScheduled > 0 ? 'warning' : 'default'} />
          <StatTile label="만료" value={formatBusinessCount(s.expiredInPeriod)} />
          <StatTile
            label="결제 문제"
            value={formatBusinessCount(s.paymentProblem)}
            hint={`기간 중 결제 실패 ${formatBusinessCount(s.failedPaymentsInPeriod)}`}
            tone={s.paymentProblem > 0 ? 'warning' : 'default'}
          />
          <StatTile label="월/연 비율" value={formatPlanMix(s.monthlyActive, s.yearlyActive)} />
          <StatTile label="이탈률" value={formatBusinessPercent(s.churnRatePercent)} />
          <StatTile label="평균 유지" value={lifetime} hint={`종료 ${formatBusinessCount(s.endedCount)}건 기준`} />
          <StatTile
            label="D14 유지"
            value={formatBusinessPercent(s.retention14d.percent)}
            hint={`${formatBusinessCount(s.retention14d.retained)} / ${formatBusinessCount(s.retention14d.eligible)} (14일 경과분)`}
          />
        </StatGrid>
        <div className="space-y-2">
          <p className="text-sm font-semibold">코호트 유지</p>
          <CohortTable subscriptions={s} />
        </div>
      </div>
    </BusinessPanel>
  );
}
